import type { SupabaseClient } from "@supabase/supabase-js";

import type {
  ExecutionProgramRepository,
  PersistentExecutionProgram,
} from "./executionProgramState";

type ProgramRow = {
  id: string;
  objective: string;
  state: string;
  requested_by: string;
  conversation_id: string | null;
  project_id: string | null;
  repository: string | null;
  branch: string | null;
  head_sha: string | null;
  current_wave: number;
  work_items: unknown;
  created_at: string;
  updated_at: string;
};

function fromRow(row: ProgramRow): PersistentExecutionProgram {
  return {
    id: row.id,

    objective: row.objective,

    state: row.state as PersistentExecutionProgram["state"],

    requestedBy: row.requested_by,

    conversationId: row.conversation_id ?? undefined,

    projectId: row.project_id ?? undefined,

    repository: row.repository ?? undefined,

    branch: row.branch ?? undefined,

    headSha: row.head_sha ?? undefined,

    currentWave: row.current_wave,

    workItems: row.work_items as PersistentExecutionProgram["workItems"],

    createdAt: row.created_at,

    updatedAt: row.updated_at,
  };
}

function toRow(program: PersistentExecutionProgram) {
  return {
    id: program.id,

    objective: program.objective,

    state: program.state,

    requested_by: program.requestedBy,

    conversation_id: program.conversationId ?? null,

    project_id: program.projectId ?? null,

    repository: program.repository ?? null,

    branch: program.branch ?? null,

    head_sha: program.headSha ?? null,

    current_wave: program.currentWave,

    work_items: program.workItems,

    created_at: program.createdAt,

    updated_at: program.updatedAt,
  };
}

export class SupabaseExecutionProgramRepository implements ExecutionProgramRepository {
  constructor(private readonly supabase: SupabaseClient) {}

  async create(program: PersistentExecutionProgram) {
    const { error } = await this.supabase
      .from("cyan_execution_programs")
      .insert(toRow(program));

    if (error) {
      throw new Error(`Unable to create execution program: ${error.message}`);
    }
  }

  async get(programId: string) {
    const { data, error } = await this.supabase
      .from("cyan_execution_programs")
      .select("*")
      .eq("id", programId)
      .maybeSingle();

    if (error) {
      throw new Error(`Unable to read execution program: ${error.message}`);
    }

    return data ? fromRow(data as ProgramRow) : null;
  }

  async save(program: PersistentExecutionProgram) {
    const { error } = await this.supabase
      .from("cyan_execution_programs")
      .update(toRow(program))
      .eq("id", program.id);

    if (error) {
      throw new Error(`Unable to save execution program: ${error.message}`);
    }
  }

  async listRunning() {
    const { data, error } = await this.supabase
      .from("cyan_execution_programs")
      .select("*")
      .in("state", ["READY", "RUNNING", "WAITING", "VERIFYING"]);

    if (error) {
      throw new Error(`Unable to list execution programs: ${error.message}`);
    }

    return (data ?? []).map((row) => fromRow(row as ProgramRow));
  }
}
